/**
 * Kernzahlen der Kapitel-Teaser auf der Übersicht /auswertungen (Persona-Umbau D6, 28.10.2026): je Kapitel EINE Zahl mit
 * Richtung und Fenster, aus bestehenden Endpunkten und derselben Rechnung wie die Kapitelseite. Rein (ohne React), damit
 * jest die Texte prüfen kann; `null` = keine Zahl (Daten fehlen) — der Teaser steht dann ohne Zahl da.
 */
import moment from 'moment';
import { formatDelta, jeTausend, tEuro } from '../../components/auswertungen/kennzahlTeile';
import type { KundentypAntwortP2, RankingAntwort, VerlaufAntwort } from '../../helpers/auswertungenData';
import type { NachfassAntwort } from '../../helpers/hebelData';
import { formatPct } from '../../helpers/kpiDefinitions';
import { formatNumber } from '../../helpers/numberUtils';
import type { RueckblickAntwort } from '../../helpers/rueckblickData';
import { euroKurz, nachfassOhneSchaetzung } from './hebel/befund';
import { bezahlquoteKernText } from './umsatz/befund';
import './kanaele/personaDaten';

export interface Kernzahl {
  /** Die große Zahl („245", „44 € gegen 18 €"). */
  wert: string;
  /** ▲/▼ oder nichts (keine Richtung bzw. nicht vom Zufall zu trennen). */
  pfeil: '▲' | '▼' | null;
  /** Was die Zahl ist, mit Fenster und Vergleich. */
  text: string;
}

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
/** „07.05." */
const tm = (iso: string): string => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
/** Exklusives Ende → letzter Tag. */
const vorTag = (iso: string): string => moment.utc(iso.slice(0, 10), 'YYYY-MM-DD').subtract(1, 'day').format('YYYY-MM-DD');

/** Letzte volle Kalenderwoche (Mo–So) vor `heute` und die 8 Wochen davor; Tage `YYYY-MM-DD`, beide Enden inklusive. */
export function letzteKw(heute: string): { kw: number; von: string; bis: string; davorVon: string; davorBis: string } {
  const montag = moment.utc(heute, 'YYYY-MM-DD').startOf('isoWeek');
  const von = montag.clone().subtract(7, 'days');
  return {
    kw: von.isoWeek(),
    von: von.format('YYYY-MM-DD'),
    bis: montag.clone().subtract(1, 'day').format('YYYY-MM-DD'),
    davorVon: von.clone().subtract(56, 'days').format('YYYY-MM-DD'),
    davorBis: von.clone().subtract(1, 'day').format('YYYY-MM-DD'),
  };
}

/**
 * Link des Bestellungen-Teasers auf genau die gezeigte KW (Audit 30.10.2026 B4) — die Seite liest `startDate`/`endDate`
 * (beide inklusive) und zeigt dann dieselben Anfos wie der Teaser.
 */
export const bestellungenLink = (w: Pick<ReturnType<typeof letzteKw>, 'von' | 'bis'>): string =>
  `/auswertungen/bestellungen?startDate=${w.von}&endDate=${w.bis}`;

/**
 * Bestellungen: Anfos der letzten KW — DIESELBE Rechnung wie die Kachel der Seite „Bestellungen" mit „Letzte KW"
 * (Ø der 12 Wochen davor, ▲/▼ nur außerhalb der Spanne). Runde 2: vorher 8 Wochen und eigene Pfeilregel, also zwei
 * Veränderungen für dieselbe Woche (+42 % hier, +27 % dort).
 */
export function bestellungenKern(
  kwAnfos: number,
  vergleich: { delta: number | null; marke: 'up' | 'down' | null; etikett: string; schnitt: number } | null,
  w: ReturnType<typeof letzteKw>,
): Kernzahl {
  return {
    wert: formatNumber(kwAnfos, 0),
    pfeil: vergleich?.marke === 'up' ? '▲' : vergleich?.marke === 'down' ? '▼' : null,
    text: `Anfos in KW ${w.kw} (${tm(w.von)}–${tm(w.bis)})${
      vergleich && vergleich.delta !== null ? ` · ${formatDelta(vergleich.delta)} ${vergleich.etikett} (Ø ${formatNumber(vergleich.schnitt, 0)})` : ''
    }`,
  };
}

/** Webinare & Produkte: Empfehlung für den nächsten Sonntag ≈ T€ (Karte „Empfehlung"), Richtung gegen den typischen Termin. */
export function webinareKern(r: RankingAntwort): Kernzahl | null {
  const e = r.empfehlung;
  const p = r.prognose;
  if (!e || !p) return null;
  let ziel: string | null = null;
  let frei = false;
  let k: { title: string; plx_product: string | null; median: number | null } | null = null;
  if (p.geplant.length === 0 && (e.wertbasis ?? 'bestellung') === 'bestellung') {
    ziel = e.ziel_datum;
    k = e.kandidaten.filter((c) => c.klasse === 'top1').sort((a, b) => (a.rang ?? 9) - (b.rang ?? 9))[0] ?? null;
  } else if (r.prognose_frei) {
    // Sonntag schon belegt: die Karte zeigt dann den nächsten freien Sonntag (`prognose_frei`).
    ziel = r.prognose_frei.zieltermin;
    frei = true;
    k = r.prognose_frei.kandidaten.filter((c) => c.rang !== null).sort((a, b) => (a.rang as number) - (b.rang as number))[0] ?? null;
  }
  if (!ziel || !k || k.median === null) return null;
  const typisch = r.typisch?.wert_summe_fest ?? null;
  const tag = `${WOCHENTAG[moment.utc(ziel, 'YYYY-MM-DD').day()]} ${tm(ziel)}`;
  return {
    wert: `≈ ${tEuro(k.median)}`,
    // Kein Pfeil an einer Prognose (Runde 2, GF): ein grünes ▲ las sich als „es läuft besser".
    pfeil: null,
    text: `Empfehlung ${frei ? 'nächster freier Sonntag, ' : ''}${tag}: ${k.title}${k.plx_product ? ` (${k.plx_product})` : ''}${typisch ? ` · typischer Termin ${tEuro(typisch)}` : ''}`,
  };
}

/**
 * Kanäle (Audit 30.10.2026 B3): Anfos je 1.000 Kontakte über die Bestell-Links der Versände der letzten KW (Karte
 * „Versände der letzten Woche", `/kanaele/rueckblick`) gegen den Schnitt der Vorwochen im GLEICHEN Alter. Vorher
 * „Direkt-Mailing gegen Heat-Up" — ein Flächenvergleich, keine Lage.
 * - Woche: Σ Anfos (ohne vorläufige, wie die Quote der Karte) ÷ Σ Kontakte × 1.000 über alle Versände mit Empfängerzahl;
 *   Versände ohne Bericht (`ohne_kontakte`) fallen ganz raus (Zähler und Nenner aus derselben Menge).
 * - Vergleich: je Versand der Schnitt der Vorwochen beim Alter dieses Versands, gewichtet mit seinen Kontakten — dieselbe
 *   Gewichtung wie die Summenzeile je Fläche (`bestellLinkAus`). Fehlt einem Versand die Basis, nur der Wochenwert.
 * - Kein Pfeil: der Vergleich ist nicht getestet.
 */
export function kanaeleKern(r: RueckblickAntwort): Kernzahl | null {
  const versand = r.bestell_link.flaechen.flatMap((f) => f.versand).filter((v) => v.kontakte > 0 && !v.ohne_kontakte);
  const kontakte = versand.reduce((s, v) => s + v.kontakte, 0);
  if (kontakte <= 0) return null;
  const quote = (versand.reduce((s, v) => s + v.anfos - v.anfos_vorlaeufig, 0) / kontakte) * 1000;
  const basis = versand.every((v) => v.vergleich.anfos_je_1000 !== null)
    ? versand.reduce((s, v) => s + v.kontakte * (v.vergleich.anfos_je_1000 as number), 0) / kontakte
    : null;
  const kw = Number(r.woche.woche.slice(-2));
  const delta = basis !== null && basis > 0 ? (quote / basis - 1) * 100 : null;
  return {
    wert: jeTausend(quote),
    pfeil: null,
    text: `Anfos je 1.000 Kontakte über Bestell-Links, Versände KW ${kw} (${tm(r.woche.von)}–${tm(vorTag(r.woche.bis))})${
      basis !== null
        ? ` · ${delta !== null ? `${formatDelta(delta)} gegen ` : ''}Ø ${jeTausend(basis)} der ${r.vorwochen} Vorwochen im gleichen Alter`
        : ''
    }`,
  };
}

/** Umsatz: Bezahlquote 12 Monate gegen dasselbe Fenster ein Jahr früher (P2 „Wer bestellt — und wer zahlt", Zeile „Alle"). */
export function umsatzKern(kt: KundentypAntwortP2): Kernzahl | null {
  const s = kt.summary;
  const davor = s.pay_rate_davor ?? null;
  if (s.pay_rate === null || davor === null) return null;
  const flag = s.test?.flag ?? null;
  return {
    wert: formatPct(s.pay_rate),
    pfeil: flag === 'up' ? '▲' : flag === 'down' ? '▼' : null,
    // Audit 30.10. B2/C2: reife Spannen beider Seiten und „Prozentpunkte" ausgeschrieben (dieselbe Rechnung wie die Kopf-Kachel).
    text: bezahlquoteKernText(kt, !!flag) ?? '',
  };
}

/**
 * Hebel (Audit 30.10.2026 B3): Ø Anfos und Erwarteter Wert je Webinar mit Nachfass aus der Nachfass-Antwort
 * (`/hebel/nachfass`) — DIESELBE Rechnung wie der Befund der Nachfass-Karte (`nachfassOhneSchaetzung`: Anfos ohne
 * vorläufige ÷ Webinare mit Nachfass, × Ø Erwarteter Wert je Nachfass-Anfo), Zähler und Nenner im selben Fenster. Kein
 * gemeinsamer Maßstab mit den Kanälen (Regel Runde 2, 28.10.: kein € je 1.000 neben Heat-Ups).
 */
export function hebelKern(n: NachfassAntwort): Kernzahl | null {
  const sch = nachfassOhneSchaetzung({
    anfos: n.summe.anfos,
    anfos_vorlaeufig: n.summe.anfos_vorlaeufig,
    wert_je_1000: n.summe.wert_je_1000,
    wert_je_anfo: n.summe.wert_je_anfo,
    heatup_wert_je_1000: null,
    webinare_im_fenster: n.webinare.im_fenster,
    webinare_mit_nachfass: n.webinare.mit_nachfass,
    ohne_nachfass: n.webinare.ohne_nachfass,
  });
  if (!sch) return null;
  const wert = n.summe.wert_je_anfo === null ? null : sch.z * n.summe.wert_je_anfo;
  return {
    wert: `Ø ${formatNumber(sch.z, 1)} Anfos${wert === null ? '' : ` / ≈ ${euroKurz(wert)}`}`,
    pfeil: null,
    text: `Nachfass an Angemeldete, je Webinar mit Nachfass (${formatNumber(n.webinare.mit_nachfass, 0)} Webinare) · ${n.window.monate} Monate, Bestellungen ${tm(n.window.von)}–${tm(vorTag(n.window.versand_bis))}`,
  };
}

// ─── Lage-Befund (Audit 30.10.2026 C1) ───────────────────────────────────────────────────────

const MONATSNAME = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
const monatsName = (month: string): string => MONATSNAME[Number(month.slice(5, 7)) - 1] ?? month;
const tageImMonat = (month: string): number => moment.utc(`${month}-01`, 'YYYY-MM-DD').daysInMonth();
const jeTag = (v: number): string => formatNumber(v, v >= 10 ? 0 : 1);

export interface LageZahlen {
  /** Laufender Monat `YYYY-MM`, gezählt bis `bis` (Tag des PL-X-Datenstands); `tage` = Tag davon. */
  monat: string;
  bis: string;
  tage: number;
  anfos: number;
  je_tag: number;
  /** Ø der letzten drei VOLLEN Monate: Σ Anfos ÷ Σ Kalendertage. */
  drei: { von: string; bis: string; anfos: number; tage: number; je_tag: number } | null;
  /** Vorjahresmonat bis zum selben Tag (`vorjahr_teilmonat` des Monatsverlaufs). */
  vorjahr: { monat: string; bis: string; anfos: number; tage: number; je_tag: number } | null;
}

/**
 * Anfos je Tag im laufenden Monat gegen den Ø der letzten drei vollen Monate und den Vorjahresmonat, aus dem
 * Monatsverlauf (`/kanaele/verlauf`). Regeln (Codex-Prüfung 30.10. #2):
 * - Der laufende Monat zählt bis zum Tag des PL-X-Datenstands; Tage = Tag des Datenstands. Liegt der Datenstand im
 *   Vormonat, gibt es keine Zahl (`'leer'`).
 * - Anfos inkl. vorläufiger, wie der Monatsverlauf (Balkenhöhe) — eine Menge, keine Quote.
 * - Kein Ausreißer-Urteil: immer alle drei Zahlen je Tag.
 * `null` = Antwort ohne laufenden Monat (unerwartete Form).
 */
export function lageZahlen(v: VerlaufAntwort): LageZahlen | 'leer' | null {
  const laufend = v.months.find((m) => m.is_partial);
  if (!laufend) return null;
  const monat = laufend.month;
  const bis = v.datenstand ? v.datenstand.slice(0, 10) : laufend.until;
  if (bis.slice(0, 7) !== monat) return 'leer';
  const tage = Number(bis.slice(8, 10));
  const anfosIn = (m: string) => v.rows.filter((r) => r.month === m).reduce((s, r) => s + r.anfos, 0);
  const anfos = anfosIn(monat);
  const davor = [3, 2, 1].map((i) => moment.utc(`${monat}-01`, 'YYYY-MM-DD').subtract(i, 'months').format('YYYY-MM'));
  const vollMonate = davor.filter((m) => v.months.some((x) => x.month === m && !x.is_partial));
  const dreiTage = vollMonate.reduce((s, m) => s + tageImMonat(m), 0);
  const dreiAnfos = vollMonate.reduce((s, m) => s + anfosIn(m), 0);
  const vt = v.vorjahr_teilmonat;
  const vtTage = vt && vt.until.slice(0, 7) === vt.month ? Number(vt.until.slice(8, 10)) : 0;
  const vtAnfos = vt ? vt.rows.reduce((s, r) => s + r.anfos, 0) : 0;
  return {
    monat,
    bis,
    tage,
    anfos,
    je_tag: anfos / tage,
    drei:
      vollMonate.length === 3 && dreiTage > 0
        ? { von: vollMonate[0], bis: vollMonate[2], anfos: dreiAnfos, tage: dreiTage, je_tag: dreiAnfos / dreiTage }
        : null,
    vorjahr: vt && vtTage > 0 ? { monat: vt.month, bis: vt.until, anfos: vtAnfos, tage: vtTage, je_tag: vtAnfos / vtTage } : null,
  };
}

/** Befundzeile der Karte „Lage" aus `lageZahlen` — Satz oder Grund, warum es keinen gibt; `null` ohne Daten. */
export function lageBefund(v: VerlaufAntwort): { text: string } | { fehlt: string } | null {
  const z = lageZahlen(v);
  if (z === null) return null;
  if (z === 'leer') {
    const laufend = v.months.find((m) => m.is_partial);
    return {
      fehlt: `Noch keine Bestellungen im ${laufend ? monatsName(laufend.month) : 'laufenden Monat'} — der Datenstand von PL-X liegt noch im Vormonat.`,
    };
  }
  const teile = [`${monatsName(z.monat)} bis ${tm(z.bis)}: ${jeTag(z.je_tag)} Anfos je Tag (${formatNumber(z.anfos, 0)} in ${z.tage} Tagen)`];
  if (z.drei) {
    const d = z.drei.je_tag > 0 ? ` (${formatDelta((z.je_tag / z.drei.je_tag - 1) * 100)})` : '';
    teile.push(`Ø ${monatsName(z.drei.von)}–${monatsName(z.drei.bis)}: ${jeTag(z.drei.je_tag)} je Tag${d}`);
  }
  if (z.vorjahr) teile.push(`${monatsName(z.vorjahr.monat)} ${z.vorjahr.monat.slice(0, 4)} bis ${tm(z.vorjahr.bis)}: ${jeTag(z.vorjahr.je_tag)} je Tag`);
  return { text: `${teile.join(' · ')}.` };
}
